import React, { useState, useEffect, useMemo, useContext } from 'react';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import MindsetResultImg from './MindsetResultImg';
import { useGroupReportDate } from '../../../../Context/GroupReportDate';
import GroupReportContext from '../../../../Context/GroupReportContext';
import { getMindsetSum } from '../../../../utils/helper';
import { logMindsetIndividualAnswers } from '../../../../utils/mindsetScoresLogger';

export default function MindsetResult({ apiData, reportType, pageNumber }) {

  const { getPercentileFromScore, averagePercentagesMindset } = useContext(GroupReportContext);

  const [isLoading, setIsLoading] = React.useState(true);
  const [is360Assessment, setIs360Assessment] = React.useState(false);
  const [hasProcessed360Data, setHasProcessed360Data] = React.useState(false);
  const { updateMindsetscoreself, updateMindsetscore } = useGroupReportDate();

  useEffect(() => {
    if (!apiData) {
      setIsLoading(true);
      return;
    }

    const has360Data =
      !!apiData?.memberResults?.allMembersData ||
      !!apiData?.memberResults?.data ||
      (Array.isArray(apiData?.memberResults?.members) &&
        apiData.memberResults.members.length > 0);

    setIs360Assessment(has360Data);

    if (reportType === "360 REPORT" && has360Data) {
      const timer = setTimeout(() => {
        setHasProcessed360Data(true);
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setHasProcessed360Data(false);
      setIsLoading(false);
    }
  }, [apiData, reportType]);

  const { selfPercentage, selfTotalScore, three60Percentage, three60TotalScore } =
    useMemo(() => {
      if (!apiData || !Array.isArray(apiData.variables)) {
        return { selfPercentage: 0, selfTotalScore: 0, three60Percentage: 0, three60TotalScore: 0 };
      }

      const maxPossibleScore = 84;

      // Self score
      const selfTotal = getMindsetSum(apiData.variables);
      const selfPct = getPercentileFromScore(selfTotal);

      let three60Pct = 0;
      let three60Total = 0;

      if (
        reportType === "360 REPORT" &&
        apiData?.memberResults &&
        is360Assessment
      ) {
        // The 360 figure is the observers' average of each observer's
        // normalized score (the respondent is excluded), as the report API
        // and the PDF calculate it. Prefer the value hydrated from the API;
        // otherwise apply the same rule to the member variables.
        const observerSums = [];
        const observerPercentages = [];
        const isSelf = (m) => ["360 Assessment", "Self-Assessment"].includes(m?.designation);
        const members = apiData.memberResults.allMembersData
          ? Object.values(apiData.memberResults.allMembersData)
          : Array.isArray(apiData.memberResults.members) ? apiData.memberResults.members : [];
        members.forEach((m) => {
          if (!m?.variables || isSelf(m)) return;
          const mindsetSum = getMindsetSum(m.variables);
          if (mindsetSum > 0) {
            observerSums.push(mindsetSum);
            observerPercentages.push(Number(getPercentileFromScore(mindsetSum)) || 0);
          }
        });

        if (observerSums.length > 0) {
          three60Total = observerSums.reduce((sum, v) => sum + v, 0) / observerSums.length;
        }
        if (typeof averagePercentagesMindset === "number" && averagePercentagesMindset > 0) {
          three60Pct = averagePercentagesMindset;
        } else if (observerPercentages.length > 0) {
          three60Pct = observerPercentages.reduce((sum, v) => sum + v, 0) / observerPercentages.length;
        }
      }

      return {
        selfPercentage: selfPct,
        selfTotalScore: selfTotal,
        three60Percentage: three60Pct,
        three60TotalScore: three60Total,
      };
    }, [apiData, reportType, is360Assessment, getPercentileFromScore, averagePercentagesMindset]);

  useEffect(() => {
    updateMindsetscoreself(typeof selfPercentage === 'number' ? selfPercentage : 8.7);
  }, [selfPercentage, updateMindsetscoreself]);

  useEffect(() => {
    updateMindsetscore(typeof three60Percentage === 'number' ? three60Percentage : 8.75);
  }, [three60Percentage, updateMindsetscore]);

  useEffect(() => {
    logMindsetIndividualAnswers({ apiData, reportType });
  }, [apiData, reportType]);

  const mindsetContent = {
    growth: {
      title: 'Your Results: Adaptive & Transformative',
      // Adaptive Learner (Light Green) - 60-73
      adaptiveBullets: [
        { label: 'Belief', text: '“My abilities can grow with effort and strategies.”' },
        { label: 'Focus', text: 'Seeks diverse challenges for development, even at the risk of failure.' },
        { label: 'Response to Feedback', text: 'Welcomes feedback as a tool for improvement.' },
        { label: 'View of Effort', text: 'Effort is necessary and rewarding for growth.' },
        { label: 'Reaction to Failure', text: 'Views failure as an opportunity to learn and adapt.' }
      ],
      // Transformative Growth (Dark Green) - 74-84
      transformativeBullets: [
        { label: 'Belief', text: '“My potential is unlimited with persistence and learning.”' },
        { label: 'Focus', text: 'Actively pursues growth in all areas, even outside comfort zones.' },
        { label: 'Response to Feedback', text: 'Actively seeks constructive criticism to refine skills.' },
        { label: 'View of Effort', text: 'Embraces effort as the core driver of improvement.' },
        { label: 'Reaction to Failure', text: 'Thrives on failure as essential for transformation.' },

      ],
      paragraphs: [
        `Your results indicate that you embody a growth mindset, aligning with the Adaptive Learner and Transformative Growth levels. This mindset reflects your belief that abilities and intelligence can be developed through effort, learning, and persistence. It's a powerful perspective that turns challenges into opportunities and views setbacks as essential steps toward mastery.`,
        `With a growth mindset, you actively embrace challenges and seek out opportunities to improve, welcoming feedback as a valuable tool for growth. You recognize effort as a pathway to progress and are not discouraged by setbacks, using them as lessons to refine your approach and build resilience.`,
        `This mindset also positively impacts those around you. By valuing collaboration and viewing others' successes as sources of inspiration rather than competition, you foster supportive and innovative environments. Your ability to integrate learning and persistence contributes to stronger relationships, teamwork, and leadership.`,
        `Your commitment to continuous improvement sets the foundation for personal and professional success. By maintaining this perspective, you are well-equipped to navigate challenges, achieve meaningful growth, and inspire others to do the same. Keep leveraging this mindset to unlock even greater potential in yourself and those you influence.`
      ]
    },
    conditional: {
      title: 'Your Results: Conditional Growth',
      bullets: [
        // Conditional Growth
        { label: 'Belief', text: `“I can grow, but only in areas where I'm naturally talented.”` },
        { label: 'Focus', text: `Selectively takes on challenges aligned with perceived strengths.` },
        { label: 'Response to Feedback', text: `Accepts feedback when it aligns with existing beliefs.` },
        { label: 'View of Effort', text: `Effort is valuable but tied to success rather than growth.` },
        { label: 'Reaction to Failure', text: `Sees failure as situational, not personal, but avoids risk.` },
      ],
      paragraphs: [
        `Your results show that you align with a mixed mindset, a mindset that combines elements of both fixed and growth perspectives. This means you believe growth is possible but often within areas where you feel naturally talented or confident. You value effort, though it's often tied to achieving success rather than the process of growth itself.`,
        `At this stage, you may prefer challenges that match your strengths and feel comfortable with feedback that reinforces what you already know. While failure doesn't feel personal, you might hesitate to take risks that lead to uncertain outcomes.`,
        `To grow further, try viewing effort as a key part of learning, not just a path to success. Take small steps to embrace challenges outside your comfort zone and seek feedback that helps you stretch and improve. Your current mindset provides a strong starting point—by focusing on progress over perfection, you can build resilience and unlock greater opportunities for growth and success.`,
        `By shifting your perspective and celebrating growth as a journey, you can start to explore new possibilities beyond your natural strengths. This mindset shift will not only enhance your personal development but also positively impact your relationships, leadership, and collaboration with others. Growth isn't just about ability—it's about discovering your potential through persistence, adaptability, and a willingness to take on new challenges.`
      ]
    },
    fixed: {
      title: 'Your Results: Rigid & Cautious',
      // Rigid Self-Definition (Dark Blue) - 12-23
      rigidBullets: [
        { label: 'Belief', text: '“My abilities and intelligence are unchangeable.”' },
        { label: 'Focus', text: 'Avoiding challenges to protect self-image.' },
        { label: 'Response to Feedback', text: 'Defensiveness or rejection of constructive criticism.' },
        { label: 'View of Effort', text: 'Sees effort as a sign of weakness.' },
        { label: 'Reaction to Failure', text: 'Internalizes failure as personal inadequacy.' }
      ],
      // Cautious Openness (Light Blue) - 24-37
      cautiousBullets: [
        { label: 'Belief', text: '“I might improve, but only within certain limits.”' },
        { label: 'Focus', text: 'Prefers familiar challenges with predictable outcomes.' },
        { label: 'Response to Feedback', text: 'Willing to listen but may struggle to act.' },
        { label: 'View of Effort', text: 'Views effort as useful but only in specific situations.' },
        { label: 'Reaction to Failure', text: 'Avoids discussing failure but acknowledges it internally.' }
      ],
      paragraphs: [
        'Your results indicate that you may currently align with aspects of a fixed mindset, particularly within the Rigid Self-Definition and Cautious Openness levels. This mindset suggests a belief that abilities and intelligence are relatively static, which can sometimes limit risk-taking and openness to challenges. Recognizing this mindset is an important step toward exploring new possibilities and fostering growth.',
        'With a fixed mindset, you might prioritize avoiding challenges to maintain comfort or protect your self-image. Setbacks may feel discouraging, but they also present opportunities for reflection and growth. Shifting your perspective to see difficult tasks as chances to develop new skills can help reframe failure as part of the learning process rather than a limitation.',
        'Taking small, intentional steps can create momentum for change. Gradually embracing challenges, seeking feedback, and celebrating progress—no matter how small—can help you move beyond the limitations of a fixed mindset. These efforts encourage a more flexible and adaptive approach to personal and professional growth.',
        'This shift not only benefits your own development but also strengthens your relationships and interactions. By viewing effort as valuable and feedback as a tool for improvement, you can begin to foster resilience and confidence in both yourself and others.',
        'Remember, growth is a journey, not a destination. By making intentional changes, you can build a mindset that helps you unlock potential, strengthen connections, and achieve meaningful progress in all areas of your life.'
      ]
    }
  };


  const getSelectedContentByPercentage = (percentage) => {
    if (percentage >= 0 && percentage < 16) {
      return mindsetContent.fixed.rigidBullets;
    } else if (percentage >= 16 && percentage < 36) {
      return mindsetContent.fixed.cautiousBullets;
    } else if (percentage >= 36 && percentage < 66) {
      return mindsetContent.conditional.bullets;
    } else if (percentage >= 66 && percentage < 86) {
      return mindsetContent.growth.adaptiveBullets;
    } else if (percentage >= 86 && percentage <= 100) {
      return mindsetContent.growth.transformativeBullets;
    }

    return []
  };

  // const selfSelectedContent = getSelectedContentByPercentage(selfPercentage);
  // const three60SelectedContent = getSelectedContentByPercentage(three60Percentage);
  const renderBullets = (percentage) => {
    const selectedContent = getSelectedContentByPercentage(Math.round(percentage));
    // console.log("percentage",percentage)
    // console.log("selectedContent",selectedContent)
    return (
      <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
        {selectedContent?.map((item, idx) => (
          <li key={idx} style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontSize: '9px', lineHeight: '13px', color: '#000', marginBottom: idx === selectedContent.length - 6 ? '0px' : '4px' }}>
            <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
          </li>
        ))}
      </ul>
    );
  };

  const renderFixedBullets = (percentage) => {
    const selectedContent = getSelectedContentByPercentage(Math.round(percentage));
    if (percentage < 24) {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent?.bullets?.slice(0, 5).map((item, idx) => (
            <li key={idx} style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '25px', color: '#5E5E5E', marginBottom: '4px' }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    } else {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent?.bullets?.slice(5).map((item, idx) => (
            <li key={idx} style={{
              fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '25px', color: '#5E5E5E', marginBottom: idx === selectedContent.bullets.length - 6 ? '0px' : '4px'
            }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    }
  };

  const renderConditionalBullets = () => {
    return (
      <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0, lineHeight: '1.5' }}>
        {mindsetContent.conditional.bullets.map((item, idx) => (
          <li key={idx} style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '25px', color: '#5E5E5E', marginBottom: idx === mindsetContent.conditional.bullets.length - 1 ? '0px' : '4px' }}>
            <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
          </li>
        ))}
      </ul>
    );
  };

  const renderMindsetCharacteristics = (percentage) => {
    // const selectedContent = getSelectedContentByPercentage(percentage);

    // if (percentage >= 60) {
    //   return renderGrowthBullets(percentage);
    // } else if (percentage >= 38) {
    //   return renderConditionalBullets();
    // } else {
    //   return renderFixedBullets(percentage);
    // }

    return renderBullets(Math.round(percentage))
  };

  const getMindsetText = (percentage) => {
    const selectedContent = getSelectedContentByPercentage(Math.round(percentage));
    return (
      <div className="self-report-text">
        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 700, fontSize: '14px', lineHeight: '18px', color: '#245775', marginBottom: '8px', display: 'block' }}>
            {selectedContent.title}
          </span>
          {selectedContent.paragraphs.map((p, idx) => (
            <p key={idx} style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '18px', color: '#245775', margin: '0 0 8px 0' }}>{p}</p>
          ))}
        </div>
      </div>
    );
  };

  const getArrowPosition = (percentage) => {
    const numPercentage = Math.round(Number(percentage));

    // Match the same ranges as getMindsetData function
    if (numPercentage >= 0 && numPercentage < 16) {
      return "23%"; // Rigid Self-Definition (Dark Blue)
    } else if (numPercentage >= 16 && numPercentage < 36) {
      return "37%"; // Cautious Openness (Light Blue)
    } else if (numPercentage >= 36 && numPercentage < 66) {
      return "51%"; // Conditional Growth (Yellow)
    } else if (numPercentage >= 66 && numPercentage < 86) {
      return "64.5%"; // Adaptive Learner (Light Green)
    } else if (numPercentage >= 86 && numPercentage <= 100) {
      return "78%"; // Transformative Growth (Dark Green)
    } else if (numPercentage > 100) {
      return "78%"; // Above maximum, use Transformative
    } else if (numPercentage < 0) {
      return "23%"; // Below minimum, use Rigid
    }

    return "50%"; // Default fallback
  };

  // console.log('Mindset Debug - SELF (Individual) totalScore:', selfTotalScore, 'percentage:', selfPercentage);
  // console.log('Mindset Debug - 360 (Average) totalScore:', three60TotalScore, 'percentage:', three60Percentage, 'is360:', is360Assessment);

  if (isLoading) {
    return (
      <div style={{
        backgroundColor: "rgb(255, 255, 255)",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Helvetica, sans-serif',
        fontSize: '14px',
        color: '#426B88'
      }}>
        Loading results...
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)" }}>
      <HeaderPreview apiData={apiData} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '16px' }}>
        {/* Always show the header for both self and 360 reports */}
        <h1 style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 700, fontSize: '14px', lineHeight: '28px', color: '#426B88', textTransform: 'uppercase', textAlign: 'center', margin: 0, letterSpacing: '0px' }}>
          Your Mindset Results (SELF)
        </h1>

        <MindsetResultImg score={selfPercentage} totalScore={selfTotalScore} />
        <div style={{ position: 'relative', background: '#EAF1F5', width: '468px', padding: '5px 10px', borderRadius: '5px', boxSizing: 'border-box', marginTop: '10px' }}>
          <div style={{ position: 'absolute', top: '-19px', left: 0, right: 0, height: '19px', overflow: 'visible', zIndex: 2 }}>
            <div style={{ width: "0px", height: "0px", borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "14px solid rgb(234, 241, 245)", position: "absolute", left: getArrowPosition(Math.round(selfPercentage)), marginLeft: "-10px", top: "6px" }} />
          </div>
          {renderMindsetCharacteristics(Math.round(selfPercentage))}
          <div style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontStyle: 'italic', fontSize: '10px', lineHeight: '14px', color: '#245775', marginTop: '4px', textAlign: 'right' }}>
            Credit: Healthy Growing Leaders
          </div>
        </div>

        {/* 360° Section - Only show if we have 360 data and it's a 360 report */}
        {is360Assessment && hasProcessed360Data && (
          <>
            <h1 style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '18px',
              color: '#00577D',
              textTransform: 'uppercase',
              textAlign: 'center',
              margin: '30px 0 10px 0',
              letterSpacing: '0px'
            }}>
              YOUR MINDSET RESULTS (360)
            </h1>

            <MindsetResultImg score={three60Percentage} totalScore={three60TotalScore} />

            <div style={{ position: 'relative', background: '#EAF1F5', width: '468px', padding: '5px 10px', borderRadius: '5px', boxSizing: 'border-box', marginTop: '10px', }}>
              <div style={{ position: 'absolute', top: '-19px', left: 0, right: 0, height: '19px', overflow: 'visible', zIndex: 2 }}>
                <div style={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "14px solid #EAF1F5", position: "absolute", left: getArrowPosition(three60Percentage), marginLeft: '-10px', top: "6px" }} />
              </div>
              {renderMindsetCharacteristics(Math.round(three60Percentage))}
              <div style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 400, fontStyle: 'italic', fontSize: '10px', lineHeight: '14px', color: '#245775', marginTop: '4px', textAlign: 'right' }}>
                Credit: Healthy Growing Leaders
              </div>
            </div>
          </>
        )}
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  )
}
